// Social Lite — photo-led social video from an order's Cat 3 photography (super admin, opened from Order Search).
// The user decides WHICH photos and in WHAT ORDER; positions map 1:1 onto the template's numbered slots
// (01 → Image_01 …); the Creatomate master template owns all motion.
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Images, Loader2, MapPin, Building2, Sparkles, Wand2, X, Check, GripVertical, Eye, Music2, Settings2, Film, AlertTriangle, RefreshCw, Plus, Download, Library, Zap, Layers, Clapperboard, FlaskConical } from "lucide-react";
import { AppHeader } from "@/AppHeader";
import { SocialLiteMusic } from "./SocialLiteMusic";
import { SocialLiteTemplatesDrawer } from "./SocialLiteTemplates";
import { SocialLiteAI } from "./SocialLiteAI";
import { SocialLiteAIAdmin } from "./SocialLiteAIAdmin";
import { pad, STATUS_COPY, RenderResult } from "./SocialLiteShared";
import "./SocialLite.css";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MODE_ICON = { slideshow: Layers, dynamic: Zap, ai_sneak_peek: Clapperboard, ai_enhance: Sparkles };
// The two AI modes share the photo/sequence/music layer with the Creatomate modes but generate moving shots per photo.
const AI_MODES = [
  { key: "ai_sneak_peek", label: "AI Sneak Peek", blurb: "Turn property photography into cinematic video", tags: ["Realistic camera moves", "Property preserved exactly"], ai: true },
  { key: "ai_enhance", label: "AI Enhance", blurb: "Bring selected property shots to life", tags: ["Fireplace", "Twilight", "Breeze", "Water"], ai: true },
];
const MODE_BLURB = { slideshow: "Clean, professional property slideshow", dynamic: "Animated property edit" };

export default function SocialLite({ onBack }) {
  const orderId = useMemo(() => { try { return sessionStorage.getItem("socialLiteOrderId") || ""; } catch { return ""; } }, []);
  const [data, setData] = useState(null);
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState([]);          // ordered photo ids = the sequence
  const [contentType, setContentType] = useState("sneak_peek");
  const [mode, setMode] = useState("slideshow");
  const [templateId, setTemplateId] = useState(null);
  const [property, setProperty] = useState({});
  const [music, setMusic] = useState(null);
  const [beatMap, setBeatMap] = useState(false);
  const [creatomateOverride, setCreatomateOverride] = useState(null); // manual pick from the Creatomate dropdown
  const [debug, setDebug] = useState(null);
  const [showDebug, setShowDebug] = useState(false);
  const [pickingMusic, setPickingMusic] = useState(false);
  const [preview, setPreview] = useState(null);
  const [suggesting, setSuggesting] = useState(null);
  const [labels, setLabels] = useState({});
  const [showTemplates, setShowTemplates] = useState(false);
  const [showAIAdmin, setShowAIAdmin] = useState(false);
  const [job, setJob] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const isAI = mode.startsWith("ai_");

  const load = useCallback(async (refresh = false) => {
    if (!orderId) { setError("Open an order from Order Search first."); return; }
    try {
      const [o, m] = await Promise.all([
        axios.get(`${API}/social-lite/orders/${orderId}`, { params: refresh ? { refresh: 1 } : {} }),
        axios.get(`${API}/social-lite/meta`),
      ]);
      setData(o.data); setMeta(m.data); setProperty(o.data.property);
    } catch (e) {
      setError(e.response?.data?.detail || "Couldn't load this order");
    }
  }, [orderId]);
  useEffect(() => { load(); }, [load]);

  const templates = useMemo(() => (meta?.templates || []).filter((t) => t.content_type === contentType && t.creative_mode === mode && t.active !== false), [meta, contentType, mode]);
  const template = templates.find((t) => t.template_id === templateId) || templates[0] || null;
  useEffect(() => { if (template && template.template_id !== templateId) setTemplateId(template.template_id); }, [template, templateId]);

  const photos = data?.photos || [];
  const byId = useMemo(() => Object.fromEntries(photos.map((p) => [p.id, p])), [photos]);
  const sequence = selected.map((id) => byId[id]).filter(Boolean);
  const max = isAI ? 14 : (template?.maximum_images ?? 12), rec = isAI ? 10 : (template?.recommended_images ?? 10), min = isAI ? 2 : (template?.minimum_images ?? 1);

  const toggle = (id) => setSelected((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : prev.length >= max ? (toast.error(`${isAI ? "AI reels take" : (template?.template_name || "This template") + " takes"} up to ${max} photographs`), prev) : [...prev, id]);
  const move = (from, to) => setSelected((prev) => { const next = [...prev]; const [it] = next.splice(from, 1); next.splice(to, 0, it); return next; });
  const replaceAt = (index, id) => setSelected((prev) => { const next = prev.filter((x, i) => x !== id || i === index); next[index] = id; return next; });

  const suggest = async (which) => {
    setSuggesting(which);
    try {
      const r = await axios.post(`${API}/social-lite/orders/${orderId}/suggest`, { count: rec, mode: which });
      setSelected(r.data.sequence.slice(0, max));
      if (r.data.labels) setLabels(r.data.labels);
      if (r.data.fallback) toast.message("AI selection unavailable — used the quick pick", { description: r.data.fallback });
      else toast.success(which === "ai" ? "Suggested a property journey — tweak anything you like" : "Quick pick applied");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't suggest a sequence");
    } finally { setSuggesting(null); }
  };

  const ct = meta?.content_types?.find((c) => c.key === contentType);
  const missing = (ct?.requires || []).filter((f) => !String(property[f] || "").trim());
  const countProblem = sequence.length < min ? `Needs at least ${min} photographs` : sequence.length > max ? `Takes up to ${max} photographs` : null;
  const effectiveCreatomateId = creatomateOverride || template?.creatomate_template_id || null;
  const creatomateName = (meta?.creatomate_templates || []).find((t) => t.id === effectiveCreatomateId)?.name;
  const canRender = !!effectiveCreatomateId && !countProblem && missing.length === 0 && !submitting && meta?.creatomate_configured;
  const renderBody = () => ({
    template_id: template.template_id,
    images: sequence.map((p) => ({ id: p.id, url: p.url, name: p.name })),
    property, music: music ? { url: music.url, name: music.name, bpm: music.bpm || null } : null,
    beat_map: beatMap && !!music, creatomate_template_id: creatomateOverride || null,
  });

  const render = async () => {
    if (!canRender) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/social-lite/orders/${orderId}/render`, renderBody());
      setJob(r.data.job);
      toast.success("Rendering — usually 1–2 minutes");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't start the render");
    } finally { setSubmitting(false); }
  };

  const loadDebug = async () => {
    if (!template) return;
    try { const r = await axios.post(`${API}/social-lite/orders/${orderId}/payload-preview`, renderBody()); setDebug(r.data); }
    catch (e) { if (showDebug) toast.error(e.response?.data?.detail || "Couldn't build the timing plan"); }
  };
  // Always keep the plan fresh (debounced) so template warnings show without opening the debug panel.
  useEffect(() => { const t = setTimeout(loadDebug, showDebug ? 150 : 600); return () => clearTimeout(t); }, [showDebug, selected, template?.template_id, music, beatMap, creatomateOverride, contentType, property]); // eslint-disable-line
  const templateWarning = debug && debug.creatomate_template_id === effectiveCreatomateId && debug.unmatched_text?.length
    ? `The Creatomate template has no ${debug.unmatched_text.join(", ")} element${debug.unmatched_text.length > 1 ? "s" : ""} — those details won't appear in the video.` : null;

  const saveAsDefault = async () => {
    try {
      await axios.put(`${API}/social-lite/templates/${template.template_id}`, { changes: { creatomate_template_id: creatomateOverride } });
      const m = await axios.get(`${API}/social-lite/meta`); setMeta(m.data); setCreatomateOverride(null);
      toast.success(`${template.template_name} now uses ${creatomateName || creatomateOverride}`);
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save"); }
  };

  useEffect(() => {
    if (!job || job.done) return undefined;
    const t = setInterval(async () => {
      try { const r = await axios.get(`${API}/social-lite/renders/${job.batch_id}`); setJob(r.data); if (r.data.in_library) load(); } catch { /* keep polling */ }
    }, 5000);
    return () => clearInterval(t);
  }, [job, load]);

  if (error) {
    return (
      <div className="slt-page" data-testid="social-lite-page">
        <AppHeader />
        <div className="slt-inner"><button className="slt-back" onClick={onBack} data-testid="social-lite-back"><ArrowLeft size={16} /> Order Search</button>
          <div className="slt-error" data-testid="social-lite-error"><AlertTriangle size={16} /> {error}</div></div>
      </div>
    );
  }
  if (!data || !meta) {
    return (
      <div className="slt-page" data-testid="social-lite-page"><AppHeader />
        <div className="slt-inner slt-loading" data-testid="social-lite-loading"><Loader2 size={22} className="slt-spin" /> Loading the order's photography…</div></div>
    );
  }
  const order = data.order;

  return (
    <div className="slt-page" data-testid="social-lite-page">
      <AppHeader />
      <div className="slt-inner">
        <div className="slt-topbar">
          <button className="slt-back" onClick={onBack} data-testid="social-lite-back"><ArrowLeft size={16} /> Order Search</button>
          <button className="slt-ghost" onClick={() => setShowTemplates(true)} data-testid="social-lite-settings"><Settings2 size={14} /> Templates</button>
          <button className="slt-ghost is-ai" onClick={() => setShowAIAdmin(true)} data-testid="social-lite-ai-admin-open"><FlaskConical size={14} /> AI models & lab</button>
        </div>
        <header className="slt-hero" data-testid="social-lite-order">
          <span className="slt-eyebrow"><Images size={12} /> Social Lite · order #{order.order_id}</span>
          <h1><MapPin size={18} /> {order.full_address}</h1>
          <div className="slt-meta">
            {order.client_name && <span><Building2 size={13} /> {order.client_name}</span>}
            {order.branch_name && <span>{order.branch_name}</span>}
            <span data-testid="social-lite-photo-count">{photos.length} Cat 3 photographs</span>
            <button className="slt-link" onClick={() => load(true)} data-testid="social-lite-refresh"><RefreshCw size={12} /> Refresh from WMS</button>
          </div>
        </header>

        <section className="slt-choices" data-testid="social-lite-choices">
          {!isAI && (
          <div className="slt-choice">
            <label>Content type</label>
            <div className="slt-seg" role="tablist">
              {meta.content_types.map((c) => (
                <button key={c.key} role="tab" aria-selected={contentType === c.key} className={`slt-seg-btn ${contentType === c.key ? "is-on" : ""}`} onClick={() => setContentType(c.key)} data-testid={`social-lite-content-${c.key}`}>{c.label}</button>
              ))}
            </div>
          </div>
          )}
          <div className="slt-choice">
            <label>Creative mode</label>
            <div className="slt-modes slt-modes-4">
              {[...meta.creative_modes, ...AI_MODES].map((m) => {
                const Icon = MODE_ICON[m.key] || Film;
                const tpls = (meta.templates || []).filter((t) => t.content_type === contentType && t.creative_mode === m.key && t.active !== false);
                const connected = tpls.filter((t) => t.connected).length;
                return (
                  <button key={m.key} className={`slt-mode ${mode === m.key ? "is-on" : ""} ${m.ai ? "is-ai" : ""}`} onClick={() => setMode(m.key)} data-testid={`social-lite-mode-${m.key}`}>
                    <span className="slt-mode-preview">{tpls[0]?.preview_image ? <img src={tpls[0].preview_image} alt="" /> : <Icon size={22} />}</span>
                    <span className="slt-mode-text">
                      <strong>{m.label}{m.ai && <em className="slt-ai-badge" data-testid={`social-lite-ai-badge-${m.key}`}>AI</em>}</strong>
                      <small>{MODE_BLURB[m.key] || m.blurb}</small>
                      <span className="slt-tags">{m.tags.map((t) => <em key={t}>{t}</em>)}</span>
                    </span>
                    {m.ai
                      ? <span className="slt-mode-state is-ready">{m.key === "ai_enhance" ? "Camera moves + chosen effects" : "Photo → subtle camera shot"}</span>
                      : <span className={`slt-mode-state ${connected ? "is-ready" : ""}`}>{connected ? `${connected} template${connected > 1 ? "s" : ""} ready` : "Template not connected yet"}</span>}
                  </button>
                );
              })}
            </div>
          </div>
          {template && !isAI && (
            <div className="slt-choice slt-template-row" data-testid="social-lite-template">
              <label>Creatomate template</label>
              <div className="slt-template-pick">
                <div className="slt-template-line">
                  {templates.length > 1 && (
                    <select value={template.template_id} onChange={(e) => { setTemplateId(e.target.value); setCreatomateOverride(null); }} data-testid="social-lite-template-select" title="Social Lite config (slot count, mappings)">
                      {templates.map((t) => <option key={t.template_id} value={t.template_id}>{t.template_name}</option>)}
                    </select>
                  )}
                  <select value={effectiveCreatomateId || ""} onChange={(e) => setCreatomateOverride(e.target.value === (template.creatomate_template_id || "") ? null : e.target.value || null)} data-testid="social-lite-creatomate-select">
                    {!effectiveCreatomateId && <option value="">— choose a Creatomate template —</option>}
                    {template.creatomate_template_id && (
                      <optgroup label={`Linked to ${template.template_name}`}>
                        <option value={template.creatomate_template_id}>{(meta.creatomate_templates || []).find((t) => t.id === template.creatomate_template_id)?.name || "Linked template"} · {template.creatomate_template_id.slice(0, 8)}…</option>
                      </optgroup>
                    )}
                    <optgroup label="All Creatomate account templates">
                      {(meta.creatomate_templates || []).filter((t) => t.id !== template.creatomate_template_id).map((t) => <option key={t.id} value={t.id}>{t.name} · {t.id.slice(0, 8)}…</option>)}
                    </optgroup>
                  </select>
                </div>
                <small className="slt-template-id" data-testid="social-lite-creatomate-id">
                  {effectiveCreatomateId ? <>Renders with <strong>{creatomateName || "Creatomate template"}</strong> · ID <code>{effectiveCreatomateId}</code>{creatomateOverride && <> · manual pick <button className="slt-link" onClick={saveAsDefault} data-testid="social-lite-creatomate-save-default">save as default for {template.template_name}</button></>}</> : "No Creatomate template linked to this config yet — pick one above (you can save it as the default)."}
                </small>
                <small>{template.description} · {template.minimum_images}–{template.maximum_images} photos, {template.recommended_images} recommended · slots {template.image_slot_names[0]} → {template.image_slot_names[template.image_slot_names.length - 1]}</small>
              </div>
            </div>
          )}
        </section>

        <div className="slt-workspace">
          <section className="slt-pane slt-available" data-testid="social-lite-available">
            <div className="slt-pane-head">
              <h2>Property photos <span>{photos.length}</span></h2>
              <div className="slt-pane-actions">
                <button className="slt-chip" onClick={() => suggest("quick")} disabled={!!suggesting} data-testid="social-lite-suggest-quick">{suggesting === "quick" ? <Loader2 size={13} className="slt-spin" /> : <Wand2 size={13} />} Quick pick {rec}</button>
                <button className="slt-chip is-ai" onClick={() => suggest("ai")} disabled={!!suggesting} data-testid="social-lite-suggest-ai">{suggesting === "ai" ? <Loader2 size={13} className="slt-spin" /> : <Sparkles size={13} />} AI property journey</button>
                {selected.length > 0 && <button className="slt-chip" onClick={() => setSelected([])} data-testid="social-lite-clear"><X size={13} /> Clear</button>}
              </div>
            </div>
            <div className="slt-grid">
              {photos.map((p) => {
                const pos = selected.indexOf(p.id);
                const lab = labels[p.id];
                return (
                  <div key={p.id} className={`slt-photo ${pos >= 0 ? "is-selected" : ""} ${p.kind !== "photo" ? "is-graphic" : ""}`} data-testid={`social-lite-photo-${p.id}`}>
                    <button className="slt-photo-btn" onClick={() => toggle(p.id)} aria-pressed={pos >= 0} data-testid={`social-lite-photo-toggle-${p.id}`}>
                      <img src={p.thumb_url} alt={p.name} loading="lazy" />
                      {pos >= 0 ? <span className="slt-pos" data-testid={`social-lite-photo-pos-${p.id}`}>{pad(pos + 1)}</span> : <span className="slt-num">#{p.number}</span>}
                      {lab?.room && <span className="slt-room">{lab.room.replace("_", " ")}</span>}
                      {pos >= 0 && <span className="slt-tick"><Check size={12} /></span>}
                    </button>
                    <button className="slt-eye" onClick={() => setPreview(p)} aria-label="Preview" data-testid={`social-lite-photo-preview-${p.id}`}><Eye size={13} /></button>
                  </div>
                );
              })}
              {photos.length === 0 && <div className="slt-empty">No Cat 3 photographs found for this order yet.</div>}
            </div>
          </section>

          <SequencePane sequence={sequence} template={isAI ? null : template} isAI={isAI} min={min} rec={rec} max={max} onMove={move} onRemove={(id) => setSelected((prev) => prev.filter((x) => x !== id))} onPreview={setPreview} labels={labels} photos={photos} onReplace={replaceAt} />
        </div>

        {isAI ? (
          <SocialLiteAI orderId={orderId} mode={mode} sequence={sequence} photos={photos} property={property} setProperty={setProperty}
            music={music} setMusic={setMusic} beatMap={beatMap} setBeatMap={setBeatMap} branchName={order.branch?.name}
            creatomateConfigured={meta.creatomate_configured} labels={labels} onOpenAdmin={() => setShowAIAdmin(true)} onLibraryChange={() => load()} />
        ) : (
        <section className="slt-pane slt-finish" data-testid="social-lite-finish">
          <div className="slt-finish-grid">
            <div className="slt-field-group">
              <label>Property details <small>filled from the order — edit freely</small></label>
              <div className="slt-fields">
                <input value={property.name || ""} onChange={(e) => setProperty({ ...property, name: e.target.value })} placeholder="Property name" data-testid="social-lite-prop-name" />
                <input value={property.location || ""} onChange={(e) => setProperty({ ...property, location: e.target.value })} placeholder="Location" data-testid="social-lite-prop-location" />
                {contentType === "price_reduced" && <>
                  <input value={property.old_price || ""} onChange={(e) => setProperty({ ...property, old_price: e.target.value })} placeholder="Was £" data-testid="social-lite-prop-old-price" />
                  <input value={property.new_price || ""} onChange={(e) => setProperty({ ...property, new_price: e.target.value })} placeholder="Now £" data-testid="social-lite-prop-new-price" />
                </>}
                {contentType === "just_listed" && <input value={property.price || ""} onChange={(e) => setProperty({ ...property, price: e.target.value })} placeholder="Price (optional)" data-testid="social-lite-prop-price" />}
                {contentType === "sold" && (
                  <div className="slt-seg slt-seg-small">
                    {["Sold", "Sold STC"].map((s) => <button key={s} className={`slt-seg-btn ${property.sold_label === s ? "is-on" : ""}`} onClick={() => setProperty({ ...property, sold_label: s })} data-testid={`social-lite-sold-${s.replace(" ", "-").toLowerCase()}`}>{s}</button>)}
                  </div>
                )}
              </div>
            </div>
            <div className="slt-field-group">
              <label><Music2 size={12} /> Music</label>
              <div className="slt-music-row">
                <button className={`slt-chip ${music ? "is-on" : ""}`} onClick={() => setPickingMusic((v) => !v)} data-testid="social-lite-music-pick">{music ? `♪ ${music.name}${music.bpm ? ` · ${music.bpm} BPM` : ""}` : "Choose a track"}</button>
                {music && <button className="slt-chip" onClick={() => setMusic(null)} data-testid="social-lite-music-clear"><X size={13} /> No music</button>}
                {music && !music.bpm && beatMap && <span className="slt-note is-warn" data-testid="social-lite-music-not-mapped">Not beat-mapped — pick a gold track for AI Beat Map</span>}
              </div>
              {pickingMusic && <SocialLiteMusic value={music} beatMapOnly={beatMap} onPick={(t) => { setMusic(t); setPickingMusic(false); }} />}

              <label className="slt-beat-label"><Zap size={12} /> AI Beat Map</label>
              <div className="slt-beat" data-testid="social-lite-beat-map">
                <div className="slt-seg slt-seg-small" role="radiogroup" aria-label="AI Beat Map">
                  <button role="radio" aria-checked={!beatMap} className={`slt-seg-btn ${!beatMap ? "is-on" : ""}`} onClick={() => setBeatMap(false)} data-testid="social-lite-beat-map-off">OFF</button>
                  <button role="radio" aria-checked={beatMap} className={`slt-seg-btn ${beatMap ? "is-on" : ""}`} onClick={() => { setBeatMap(true); if (!music || !music.bpm) { if (music) setMusic(null); setPickingMusic(true); } }} data-testid="social-lite-beat-map-on">ON</button>
                </div>
                <small className="slt-note" data-testid="social-lite-beat-map-note">
                  {!beatMap ? (music ? "Uses the template's timing, stretched to its target length." : "Template timing. Choose a track to add music — gold tracks are beat-mapped.")
                    : !music ? "Pick a beat-mapped (gold) track above — the edit is timed to its beats."
                    : music.bpm ? `Times the edit to “${music.name}” · ${music.bpm} BPM · ${mode === "dynamic" ? "Dynamic profile: strong beats, ~0.5–1.5s per image" : "Slideshow profile: bars & phrases, ~1.5–3s per image"}, aiming for the template's ${template?.default_duration || (mode === "dynamic" ? 15 : 20)}s. The template still owns the look and movement.`
                    : "No beat analysis for this track — the template's timing will be used (safe fallback)."}
                </small>
              </div>
            </div>
          </div>

          <div className="slt-render-row">
            <button className="slt-render" disabled={!canRender} onClick={render} data-testid="social-lite-render">
              {submitting ? <Loader2 size={16} className="slt-spin" /> : <Film size={16} />} Generate {ct?.label} · {meta.creative_modes.find((m) => m.key === mode)?.label}
            </button>
            <span className="slt-render-note" data-testid="social-lite-render-note">
              {!effectiveCreatomateId ? "Pick a Creatomate template above (or link one in Templates) and this button unlocks."
                : countProblem ? countProblem
                : missing.length ? `Add the ${missing.map((m) => m.replace("_", " ")).join(" and ")}`
                : `${sequence.length} photographs → ${template.image_slot_names[0]}–${template.image_slot_names[Math.min(sequence.length, max) - 1]} · ${beatMap && music ? "AI Beat Map on" : "template timing"}${debug?.visual_duration && debug.creatomate_template_id === effectiveCreatomateId ? ` · ≈${Number(debug.visual_duration).toFixed(1)}s` : ""}${music ? " · with music" : " · no music"}. Usually 1–2 minutes; it lands in ${order.branch?.name || "the branch"}'s Social Library.`}
            </span>
            <button className="slt-chip" onClick={() => setShowDebug((v) => !v)} data-testid="social-lite-debug-toggle"><Settings2 size={13} /> {showDebug ? "Hide" : "Timing plan & payload"}</button>
          </div>
          {templateWarning && <div className="slt-warn" data-testid="social-lite-template-warning"><AlertTriangle size={14} /> {templateWarning}</div>}
          {showDebug && <TimingDebug debug={debug} music={music} />}
          {job && <RenderResult job={job} />}
          {data.renders?.length > 0 && (
            <div className="slt-previous" data-testid="social-lite-previous">
              <label>Made from this order</label>
              {data.renders.map((r) => (
                <div key={r.job_id} className={`slt-prev-row is-${r.status}`} data-testid={`social-lite-previous-${r.job_id}`}>
                  {r.snapshot_url ? <img src={r.snapshot_url} alt="" /> : <span className="slt-prev-ph"><Film size={14} /></span>}
                  <div><strong>{r.label}</strong><small>{r.template_name} · {r.image_count} photos · {STATUS_COPY[r.status] || r.status}</small></div>
                  {r.status === "succeeded" && r.url && <a className="slt-mini" href={r.r2_url || r.url} target="_blank" rel="noreferrer" data-testid={`social-lite-previous-open-${r.job_id}`}><Download size={13} /> Open</a>}
                  {r.library_asset_id && <span className="slt-lib"><Library size={12} /> In library</span>}
                </div>
              ))}
            </div>
          )}
        </section>
        )}
      </div>

      {preview && (
        <div className="slt-lightbox" onClick={() => setPreview(null)} data-testid="social-lite-lightbox">
          <img src={preview.url} alt={preview.name} />
          <div className="slt-lightbox-cap">#{preview.number} · {preview.name}{labels[preview.id]?.room ? ` · ${labels[preview.id].room.replace("_", " ")}` : ""}</div>
          <button className="slt-lightbox-close" aria-label="Close" data-testid="social-lite-lightbox-close"><X size={18} /></button>
        </div>
      )}
      {showTemplates && <SocialLiteTemplatesDrawer meta={meta} onClose={() => setShowTemplates(false)} onChanged={(m) => setMeta(m)} />}
      {showAIAdmin && <SocialLiteAIAdmin photos={photos} onClose={() => setShowAIAdmin(false)} />}
    </div>
  );
}

function TimingDebug({ debug, music }) {
  if (!debug) return <div className="slt-debug" data-testid="social-lite-debug"><Loader2 size={14} className="slt-spin" /> Building the timing plan…</div>;
  const p = debug.timing_plan || {};
  const modKeys = Object.keys(debug.modifications || {});
  return (
    <div className="slt-debug" data-testid="social-lite-debug">
      <div className="slt-debug-grid">
        <div><span>AI Beat Map</span><strong data-testid="social-lite-debug-mode">{p.mode === "beat_map" ? "ON" : "OFF"}{p.fallback ? " · fallback" : ""}</strong></div>
        <div><span>Profile</span><strong>{p.profile?.label || p.creative_mode}</strong></div>
        <div><span>Template</span><strong>{p.template_name}</strong></div>
        <div><span>Creatomate ID</span><strong><code>{debug.creatomate_template_id || "—"}</code></strong></div>
        <div><span>Music</span><strong>{music ? `${music.name}${p.bpm ? ` · ${p.bpm} BPM` : ""}` : "none"}</strong></div>
        <div><span>Duration</span><strong data-testid="social-lite-debug-duration">{debug.visual_duration ? `${Number(debug.visual_duration).toFixed(2)}s` : p.duration ? `${Number(p.duration).toFixed(2)}s` : "template"}{debug.target_duration ? ` / target ${debug.target_duration}s` : ""}{debug.fitted_scale ? ` · template ×${debug.fitted_scale}` : ""}</strong></div>
        <div><span>Render path</span><strong data-testid="social-lite-debug-path">{debug.render_path === "source" ? "live template source" : "hosted modifications"}</strong></div>
        <div><span>Music track</span><strong data-testid="social-lite-debug-music">{debug.music_delivery === "audio_track" ? "added as audio track" : debug.music_delivery === "template_slot" ? "template music slot" : "none"}</strong></div>
        <div><span>Unused slots</span><strong data-testid="social-lite-debug-trimmed">{debug.trimmed_slots?.length ? `trimmed ${debug.trimmed_slots[0]}–${debug.trimmed_slots[debug.trimmed_slots.length - 1]}` : "none"}</strong></div>
        <div><span>No element for</span><strong data-testid="social-lite-debug-unmatched">{debug.unmatched?.length ? debug.unmatched.join(", ") : "—"}</strong></div>
      </div>
      <p className="slt-debug-reason">{p.reason}{p.profile && p.mode === "beat_map" ? ` · target ${p.profile.target_min}–${p.profile.target_max}s · uses ${p.profile.uses}` : ""}{debug.problem ? ` · ⚠ ${debug.problem}` : ""}{debug.source_error ? ` · ⚠ template fetch failed: ${debug.source_error}` : ""}</p>
      {p.mode === "beat_map" && (
        <div className="slt-debug-cols">
          <div><span>Edit points</span><pre data-testid="social-lite-debug-edit-points">{(p.edit_points || []).map((t) => Number(t).toFixed(3).padStart(6, "0")).join("\n")}</pre></div>
          <div><span>Image durations</span><pre data-testid="social-lite-debug-durations">{(p.image_timings || []).map((t) => `${t.slot} = ${Number(t.duration).toFixed(2)}s (${t.beats} beat${t.beats === 1 ? "" : "s"})`).join("\n")}</pre></div>
        </div>
      )}
      <div><span className="slt-debug-key">Modifications sent ({modKeys.length})</span><pre className="slt-debug-mods" data-testid="social-lite-debug-mods">{modKeys.map((k) => `${k}: ${String(debug.modifications[k]).slice(0, 70)}`).join("\n")}</pre></div>
    </div>
  );
}

function SequencePane({ sequence, template, isAI, min, rec, max, onMove, onRemove, onPreview, labels, photos, onReplace }) {
  const dragIdx = useRef(null);
  const [dragging, setDragging] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const slots = template?.image_slot_names || (isAI ? sequence.map((_, i) => `Shot ${pad(i + 1)}`) : []);
  const n = sequence.length;
  const status = n >= max ? `${n} / ${max} images selected` : `${n} / ${rec} recommended images selected`;
  const hint = n < min ? `${min - n} more needed (minimum ${min})` : n < rec ? `${rec - n} more recommended` : n < max ? `room for ${max - n} more` : (isAI ? "reel full" : "template full");
  const onDragEnter = (idx) => { if (dragIdx.current === null || dragIdx.current === idx) return; onMove(dragIdx.current, idx); dragIdx.current = idx; setDragging(idx); };
  const end = () => { dragIdx.current = null; setDragging(null); };
  return (
    <section className="slt-pane slt-sequence" data-testid="social-lite-sequence">
      <div className="slt-pane-head">
        <h2>Your sequence</h2>
        <div className="slt-count" data-testid="social-lite-count">
          <strong>{status}</strong>
          <small className={n < min ? "is-bad" : ""}>{hint}</small>
        </div>
      </div>
      <div className="slt-progress"><div className={`slt-progress-fill ${n < min ? "is-bad" : n >= rec ? "is-good" : ""}`} style={{ width: `${Math.min(100, (n / max) * 100)}%` }} /></div>
      <p className="slt-seq-hint">{isAI ? "Drag to reorder — each position becomes one AI-generated shot, in this order. Set the camera move per shot below." : <>Drag to reorder — position 01 becomes {slots[0] || "Image_01"}, 02 becomes {slots[1] || "Image_02"}, and so on. The template decides how each photo moves.</>}</p>
      <ol className="slt-seq-list">
        {sequence.map((p, idx) => (
          <li key={p.id} className={`slt-seq-item ${dragging === idx ? "is-dragging" : ""}`} draggable
            onDragStart={(e) => { dragIdx.current = idx; setDragging(idx); e.dataTransfer.effectAllowed = "move"; }}
            onDragEnter={() => onDragEnter(idx)} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); end(); }} onDragEnd={end}
            data-testid={`social-lite-seq-${idx}`}>
            <span className="slt-seq-pos" data-testid={`social-lite-seq-pos-${idx}`}>{pad(idx + 1)}</span>
            <img src={p.thumb_url} alt="" />
            <div className="slt-seq-text">
              <strong>{slots[idx] || `Image_${pad(idx + 1)}`}</strong>
              <small>#{p.number}{labels[p.id]?.room ? ` · ${labels[p.id].room.replace("_", " ")}` : ""}</small>
            </div>
            <div className="slt-seq-actions">
              <button onClick={() => idx > 0 && onMove(idx, idx - 1)} disabled={idx === 0} aria-label="Move up" data-testid={`social-lite-seq-up-${idx}`}>↑</button>
              <button onClick={() => idx < n - 1 && onMove(idx, idx + 1)} disabled={idx === n - 1} aria-label="Move down" data-testid={`social-lite-seq-down-${idx}`}>↓</button>
              <button onClick={() => setReplacing(replacing === idx ? null : idx)} aria-label="Replace" data-testid={`social-lite-seq-replace-${idx}`}><RefreshCw size={12} /></button>
              <button onClick={() => onPreview(p)} aria-label="Preview" data-testid={`social-lite-seq-preview-${idx}`}><Eye size={12} /></button>
              <button className="is-remove" onClick={() => onRemove(p.id)} aria-label="Remove" data-testid={`social-lite-seq-remove-${idx}`}><X size={12} /></button>
            </div>
            <GripVertical size={14} className="slt-grip" />
            {replacing === idx && (
              <div className="slt-replace" data-testid={`social-lite-replace-${idx}`}>
                {photos.filter((x) => !sequence.some((s) => s.id === x.id) && x.kind === "photo").map((x) => (
                  <button key={x.id} onClick={() => { onReplace(idx, x.id); setReplacing(null); }} data-testid={`social-lite-replace-with-${x.id}`}><img src={x.thumb_url} alt="" /><span>#{x.number}</span></button>
                ))}
              </div>
            )}
          </li>
        ))}
        {n === 0 && <li className="slt-empty">Pick photographs on the left, or let AI suggest a property journey.</li>}
        {n > 0 && n < max && <li className="slt-seq-ghost" data-testid="social-lite-seq-next"><span className="slt-seq-pos is-ghost">{pad(n + 1)}</span><Plus size={14} /> {isAI ? `Shot ${pad(n + 1)}` : (slots[n] || `Image_${pad(n + 1)}`)} — add another photo</li>}
      </ol>
    </section>
  );
}
